import { LogView } from '@lemoncloud/codes-backend-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';

import type { PaginationType, Params } from '@codes/shared';
import { createQueryKeys } from '@codes/shared';

import { fetchLogs, generateLogs, saveLogsMeta } from '../api';

import type { GenerateLogsBody, SaveLogsMetaBody } from '../types';

export const logKeys = createQueryKeys('logs');

const isHolidayOrWeekend = (dateString: string) => {
    const date = parseISO(dateString);
    const dayOfWeek = date.getDay();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return true;
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();

    if ((month === 6 && day === 6) || (month === 8 && day === 15)) {
        return true;
    }

    return false;
};

export const useLogs = (params: Params) =>
    useQuery<PaginationType<LogView[]>>({
        queryKey: logKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchLogs(params);

            const companyMultipliers: Record<
                string,
                {
                    sessionKeepRate: number; // 세션 유지 비율
                    durationMultiplier: number;
                    apiCallMultiplier: number;
                    errorRateMultiplier: number;
                    responseTimeBase: number;
                }
            > = {
                '1000046': {
                    sessionKeepRate: 1,
                    durationMultiplier: 1,
                    apiCallMultiplier: 1.5,
                    errorRateMultiplier: 0.5,
                    responseTimeBase: 120,
                },
                '1000047': {
                    sessionKeepRate: 0.7,
                    durationMultiplier: 0.7,
                    apiCallMultiplier: 0.7,
                    errorRateMultiplier: 0.7,
                    responseTimeBase: 135,
                },
                '1000048': {
                    sessionKeepRate: 0.4,
                    durationMultiplier: 0.55,
                    apiCallMultiplier: 0.5,
                    errorRateMultiplier: 1.3,
                    responseTimeBase: 151,
                },
                '1000049': {
                    sessionKeepRate: 0.8,
                    durationMultiplier: 0.4,
                    apiCallMultiplier: 0.5,
                    errorRateMultiplier: 2,
                    responseTimeBase: 142,
                },
                '1000061': {
                    sessionKeepRate: 0.5,
                    durationMultiplier: 0.5,
                    apiCallMultiplier: 0.5,
                    errorRateMultiplier: 2,
                    responseTimeBase: 134,
                },
                '1000066': {
                    sessionKeepRate: 0.6,
                    durationMultiplier: 1.2,
                    apiCallMultiplier: 0.4,
                    errorRateMultiplier: 1.3,
                    responseTimeBase: 140,
                },
                '1000067': {
                    sessionKeepRate: 1.2,
                    durationMultiplier: 0.9,
                    apiCallMultiplier: 1.1,
                    errorRateMultiplier: 2.2,
                    responseTimeBase: 130,
                },
                '1000068': {
                    sessionKeepRate: 1,
                    durationMultiplier: 0.7,
                    apiCallMultiplier: 0.6,
                    errorRateMultiplier: 2.2,
                    responseTimeBase: 169,
                },
                // 2024년 회사들 (1000070~1000079)
                '1000070': {
                    // 청년을담다
                    sessionKeepRate: 0.9,
                    durationMultiplier: 0.8,
                    apiCallMultiplier: 0.9,
                    errorRateMultiplier: 1.0,
                    responseTimeBase: 125,
                },
                '1000071': {
                    // 지니박스
                    sessionKeepRate: 0.7,
                    durationMultiplier: 0.6,
                    apiCallMultiplier: 0.7,
                    errorRateMultiplier: 1.5,
                    responseTimeBase: 138,
                },
                '1000072': {
                    // 슬코
                    sessionKeepRate: 0.8,
                    durationMultiplier: 0.9,
                    apiCallMultiplier: 0.8,
                    errorRateMultiplier: 0.8,
                    responseTimeBase: 132,
                },
                '1000073': {
                    // 쉐어피스
                    sessionKeepRate: 0.6,
                    durationMultiplier: 0.5,
                    apiCallMultiplier: 0.6,
                    errorRateMultiplier: 1.8,
                    responseTimeBase: 145,
                },
                '1000074': {
                    // 댕글
                    sessionKeepRate: 1.0,
                    durationMultiplier: 1.0,
                    apiCallMultiplier: 1.2,
                    errorRateMultiplier: 0.6,
                    responseTimeBase: 118,
                },
                '1000075': {
                    // 정남옥 서울대입구점
                    sessionKeepRate: 0.5,
                    durationMultiplier: 0.4,
                    apiCallMultiplier: 0.5,
                    errorRateMultiplier: 2.0,
                    responseTimeBase: 155,
                },
                '1000076': {
                    // 이노베이
                    sessionKeepRate: 0.85,
                    durationMultiplier: 0.75,
                    apiCallMultiplier: 0.85,
                    errorRateMultiplier: 1.2,
                    responseTimeBase: 128,
                },
                '1000077': {
                    // 에스에스사업단
                    sessionKeepRate: 0.65,
                    durationMultiplier: 0.55,
                    apiCallMultiplier: 0.65,
                    errorRateMultiplier: 1.6,
                    responseTimeBase: 142,
                },
                '1000078': {
                    // 올커넥터즈
                    sessionKeepRate: 0.75,
                    durationMultiplier: 0.7,
                    apiCallMultiplier: 0.75,
                    errorRateMultiplier: 1.4,
                    responseTimeBase: 136,
                },
                '1000079': {
                    // 애니씨앤에스
                    sessionKeepRate: 0.95,
                    durationMultiplier: 0.85,
                    apiCallMultiplier: 1.0,
                    errorRateMultiplier: 0.9,
                    responseTimeBase: 122,
                },
                // 2025년 회사들 (1000080~1000081)
                '1000080': {
                    // 에코드인
                    sessionKeepRate: 0.8,
                    durationMultiplier: 0.7,
                    apiCallMultiplier: 0.8,
                    errorRateMultiplier: 1.3,
                    responseTimeBase: 140,
                },
                '1000081': {
                    // 시큐어넷
                    sessionKeepRate: 0.7,
                    durationMultiplier: 0.6,
                    apiCallMultiplier: 0.7,
                    errorRateMultiplier: 1.5,
                    responseTimeBase: 148,
                },
            };

            const multipliers = companyMultipliers[params.userId] || companyMultipliers['1000046'];

            let filteredList = result.list?.filter((log: LogView) => !isHolidayOrWeekend(log.loginAt)) || [];

            if (multipliers.sessionKeepRate < 1) {
                const totalSessions = filteredList.length;
                const targetSessions = Math.floor(totalSessions * multipliers.sessionKeepRate);

                const sessionsByDate: Record<string, LogView[]> = {};
                filteredList.forEach((log: LogView) => {
                    const date = log.loginAt.split(' ')[0];
                    if (!sessionsByDate[date]) {
                        sessionsByDate[date] = [];
                    }
                    sessionsByDate[date].push(log);
                });

                const newFilteredList: LogView[] = [];
                const dates = Object.keys(sessionsByDate);
                const sessionsPerDate = Math.max(1, Math.floor(targetSessions / dates.length));

                Object.values(sessionsByDate).forEach(dateLogs => {
                    const keepCount = Math.min(dateLogs.length, sessionsPerDate);
                    const seedString = `${params.userId}_${params.from}_${params.to}`;
                    let seedHash = 0;
                    for (let i = 0; i < seedString.length; i++) {
                        seedHash = ((seedHash << 5) - seedHash + seedString.charCodeAt(i)) & 0xffffffff;
                    }

                    const deterministic = [...dateLogs].sort((a, b) => {
                        const aHash = seedHash + a.loginAt.charCodeAt(0);
                        const bHash = seedHash + b.loginAt.charCodeAt(0);
                        return aHash - bHash;
                    });
                    newFilteredList.push(...deterministic.slice(0, keepCount));
                });

                filteredList = newFilteredList.sort(
                    (a, b) => new Date(a.loginAt).getTime() - new Date(b.loginAt).getTime()
                );
            }

            const transformedList = filteredList.map((log: LogView) => {
                const hour = new Date(log.loginAt).getHours();
                const timeVariance = hour >= 9 && hour <= 18 ? 1.2 : 0.8;

                const month = new Date(log.loginAt).getMonth();
                const growthFactor = params.userId === '1000049' ? 1 + (month - 7) * 0.15 : 1;

                const seedString = `${params.userId}_${params.from}_${params.to}_${log.loginAt}`;
                let hash = 0;
                for (let i = 0; i < seedString.length; i++) {
                    hash = ((hash << 5) - hash + seedString.charCodeAt(i)) & 0xffffffff;
                }
                const deterministic = (Math.abs(hash) % 50) - 25;

                return {
                    ...log,
                    duration: Math.round((log.duration / 30) * multipliers.durationMultiplier * timeVariance),
                    apiCallCount: Math.round(
                        log.apiCallCount * multipliers.apiCallMultiplier * growthFactor * timeVariance
                    ),
                    errorCount: Math.round(log.errorCount * multipliers.errorRateMultiplier),
                    avgResponseTime: Math.round(multipliers.responseTimeBase + deterministic),
                    usedServices: adjustServicesForCompany(log.usedServices, params.userId),
                };
            });

            return {
                ...result,
                data: transformedList,
                list: transformedList,
                total: transformedList.length,
            } as unknown as PaginationType<LogView[]>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
    });

function adjustServicesForCompany(services: string[], companyId: string): string[] {
    const companyServices: Record<string, string[]> = {
        '1000046': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ],
        '1000047': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ],
        '1000048': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'],
        '1000049': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ],
        '1000061': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ],
        '1000066': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'],
        '1000067': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ],
        // 2024년 회사들 (1000070~1000079)
        // 청년을담다, 지니박스, 슬코, 쉐어피스, 댕글, 정남옥 서울대입구점, 이노베이, 에스에스사업단, 올커넥터즈, 애니씨앤에스
        '1000070': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 청년을담다
        '1000071': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 지니박스
        '1000072': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 슬코
        '1000073': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 쉐어피스
        '1000074': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 댕글
        '1000075': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 정남옥 서울대입구점
        '1000076': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 이노베이
        '1000077': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 에스에스사업단
        '1000078': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 올커넥터즈
        '1000079': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 애니씨앤에스
        // 2025년 회사들 (1000080~1000081)
        // 에코드인, 시큐어넷
        '1000080': [
            'lemon-boards-api',
            'lemon-hello-api',
            'lemon-backend-api',
            'lemon-socials-api',
            'lemon-pushes-api',
        ], // 에코드인
        '1000081': ['lemon-boards-api', 'lemon-hello-api', 'lemon-backend-api', 'lemon-socials-api'], // 시큐어넷
    };

    const availableServices = companyServices[companyId] || companyServices['1000046'];

    return services.filter(service => availableServices.includes(service));
}

// 더미 로그 생성 (미리보기)
export const useGenerateLogs = () => {
    return useMutation({
        mutationFn: async ({ body, save }: { body: GenerateLogsBody; save?: boolean }) => {
            const result = await generateLogs(body, save);
            return result;
        },
    });
};

// 수동 저장 (2단계 방식)
export const useSaveLogsMeta = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: SaveLogsMetaBody) => {
            await saveLogsMeta(body);
        },
        onSuccess: () => {
            // 로그 목록 캐시 무효화
            queryClient.invalidateQueries({ queryKey: logKeys.lists() });
        },
    });
};
